import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { Prompt } from '@/features/assistant/components/Prompt';
import { type TabType, Tabs } from '@/features/assistant/components/Tabs';
import { FilesUploadedListDropdown } from '@/features/assistant/components/UploadedFilesDropdown';
import { useFileUploads } from '@/features/attachments/hooks/useFileUploads';

export interface AssistantBottomSectionHandle {
  clearState: () => void;
  mergeApiAttachments: (
    attachments: Array<{
      fileName: string;
      fileSize?: number;
      id: string;
      mimeType?: string;
      url?: string;
    }>,
  ) => void;
  setPrompt: (value: string) => void;
}

interface AssistantBottomSectionProps {
  isLoading: boolean;
  onSend: (message: string, attachmentIds: Array<string>) => void;
  onStop: () => void;
  setShowContextDialog: (open: boolean) => void;
  showContextDialog: boolean;
}

export const AssistantBottomSection = forwardRef<
  AssistantBottomSectionHandle,
  AssistantBottomSectionProps
>(function AssistantBottomSection(
  { isLoading, onSend, onStop, setShowContextDialog, showContextDialog },
  ref,
) {
  const posthog = usePostHog();
  const filesSectionRef = useRef<HTMLDivElement>(null);

  const [tabContext, setTabContext] = useState<TabType>('email-dropdown');
  const [promptValue, setPromptValue] = useState('');
  const [promptKey, setPromptKey] = useState(0);

  const handleFilesCountChange = useCallback((count: number) => {
    if (count === 0) {
      setTabContext('email-dropdown');
    }
  }, []);

  const {
    attachmentIds,
    bulkUploadMutation,
    clearAttachments: clearAttachmentsFromHook,
    handleClearAll,
    handleFileDelete,
    handleFileRetry,
    handleFilesAdded: handleFilesAddedFromHook,
    handleFileView,
    mergeApiAttachments,
    setShowFilesUploaded,
    showFilesUploaded,
    uploadedFiles,
    uploadMutation,
  } = useFileUploads({ onFilesChange: handleFilesCountChange });

  const clearAttachments = useCallback(() => {
    clearAttachmentsFromHook();
    setTabContext((prev) => (prev === 'files-dropdown' ? 'email-dropdown' : prev));
  }, [clearAttachmentsFromHook]);

  useImperativeHandle(
    ref,
    () => ({
      clearState: () => {
        clearAttachments();
        setPromptValue('');
        setPromptKey((prev) => prev + 1);
        setTabContext('email-dropdown');
        setShowContextDialog(false);
      },
      mergeApiAttachments,
      setPrompt: (value) => {
        setPromptValue(value);
        setPromptKey((prev) => prev + 1);
      },
    }),
    [clearAttachments, mergeApiAttachments, setShowContextDialog],
  );

  const handleContextChange = useCallback(
    (value: TabType) => {
      if (value === tabContext) {
        if (value === 'email-dropdown') {
          setShowContextDialog(!showContextDialog);
        } else if (value === 'files-dropdown') {
          setShowFilesUploaded(!showFilesUploaded);
        }
      } else {
        setTabContext(value);
        if (value === 'email-dropdown') {
          setShowFilesUploaded(false);
          setShowContextDialog(true);
        } else if (value === 'files-dropdown') {
          setShowContextDialog(false);
          setShowFilesUploaded(true);
        }
      }
    },
    [setShowContextDialog, setShowFilesUploaded, showContextDialog, showFilesUploaded, tabContext],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-tabs-container]')) {
        return;
      }
      if (target.closest('[data-radix-popper-content-wrapper]')) {
        return;
      }
      if (
        target.closest('[data-slot="dialog-content"]') ||
        target.closest('[data-slot="dialog-overlay"]')
      ) {
        return;
      }
      if (showContextDialog) {
        setShowContextDialog(false);
      }
      if (showFilesUploaded) {
        setShowFilesUploaded(false);
      }
    };

    if (showContextDialog || showFilesUploaded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [setShowContextDialog, setShowFilesUploaded, showContextDialog, showFilesUploaded]);

  const handleFilesAdded = useCallback(
    async (files: Array<File>) => {
      setTabContext('files-dropdown');
      setShowFilesUploaded(true);
      setShowContextDialog(false);
      if (files.length > 0) {
        const totalSize = files.reduce((sum, f) => sum + f.size, 0);
        const mimeTypes = Array.from(new Set(files.map((f) => f.type || 'unknown')));
        posthog.capture(ANALYTICS_EVENTS.ASSISTANT_FILES_UPLOADED, {
          fileCount: files.length,
          mimeTypes,
          totalSize,
        });
      }
      await handleFilesAddedFromHook(files);
    },
    [handleFilesAddedFromHook, posthog, setShowContextDialog, setShowFilesUploaded],
  );

  const handleSend = useCallback(
    (message: string) => {
      onSend(message, attachmentIds);
      setPromptValue('');
    },
    [attachmentIds, onSend],
  );

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-col overflow-hidden">
      <div className="relative mb-2 w-full" ref={filesSectionRef}>
        <FilesUploadedListDropdown
          containerRef={filesSectionRef}
          isUploadPending={uploadMutation.isPending || bulkUploadMutation.isPending}
          onClearAll={handleClearAll}
          onFileDelete={handleFileDelete}
          onFileRetry={handleFileRetry}
          onFileView={handleFileView}
          onOpenChange={setShowFilesUploaded}
          open={showFilesUploaded}
          uploadedFiles={uploadedFiles}
        />
      </div>

      <Tabs
        isEmailContextDialogOpen={showContextDialog}
        isFilesUploadedListDropdownOpen={showFilesUploaded}
        onValueChange={handleContextChange}
        showFilesUploaded={uploadedFiles.length > 0}
        value={tabContext}
      />

      <Prompt
        initialValue={promptValue}
        isStreaming={isLoading}
        key={promptKey}
        onAttachmentClick={() => undefined}
        onFilesAdded={handleFilesAdded}
        onSend={handleSend}
        onStop={onStop}
      />
    </div>
  );
});
